import dayjs from "dayjs";
import { Job } from "interfaces/jobs";
import isBetween from "dayjs/plugin/isBetween";

dayjs.extend(isBetween);

/*
Filters the jobpostings on searchterm and categories, as well as sorting the 
results based on deadline
*/
export const getFilteredJobPostings = (
  jobPostings: Job[],
  searchTerm: string,
  jobTypeChecked: string,
  sectorsChecked: number[],
  locationChecked: string[],
  yearLevelsChecked: number[],
  publishedThisWeekChecked: boolean,
  deadlineChecked: string
) =>
  jobPostings
    .filter(
      // Filter based on search term (searches in both title and company name)
      jobPosting =>
        searchTerm === "" ||
        jobPosting.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        jobPosting.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .filter(
      /* 
      Filter on year level. 
      If no year level is checked, return all job postings. 
      Else the job posting has to be open for the year checked for it to be shown
      */
      jobPosting => !yearLevelsChecked.length || jobPosting.year_levels.some(year => yearLevelsChecked.includes(year))
    )
    .filter(
      /* 
      Filter on job type. 
      If "Alle stillingstyper" is checked, return all job postings. 
      Else the checked job type has to match the determined job type in the job posting for it to be shown
      */
      jobPosting =>
        jobTypeChecked === "Alle stillingstyper" ||
        (jobTypeChecked === "Fast stilling" && jobPosting.job_type === "F") ||
        (jobTypeChecked === "Internship" && jobPosting.job_type === "S") ||
        (jobTypeChecked === "Trainee" && jobPosting.job_type === "T") ||
        (jobTypeChecked === "Diplomoppgave" && jobPosting.job_type === "E") ||
        (jobTypeChecked === "Deltid" && jobPosting.job_type === "D") ||
        (jobTypeChecked === "Annet" && jobPosting.job_type === "A")
    )
    .filter(
      /* 
      Filter on work location. 
      If no work location is checked, return all job postings. 
      If "annet" is checked, only return job posting if jobposing location is somthing else than the given list
      Else return job posing if job posting location is one of the checked locations
      */
      jobPosting => {
        if (!locationChecked.length) return true;
        if (jobPosting.location === null) return false;
        if (locationChecked.some(location => jobPosting.location.toLowerCase().includes(location.toLowerCase())))
          return true;
        if (
          locationChecked.includes("Annet") &&
          !["Oslo", "Trondheim", "Bergen", "Stavanger", "Kristianand", "Trømsø"].some(location =>
            jobPosting.location.toLowerCase().includes(location.toLowerCase())
          ) &&
          jobPosting.location.length !== 0
        )
          return true;
        return false;
      }
    )
    .filter(
      /* 
      Filter on deadline. 
      If no deadline is checked, return all job postings. 
      Else deadline checked has to match job positings intervall until deadline for it to be shown
      */
      jobPosting =>
        deadlineChecked === "" ||
        (deadlineChecked === "Siste frist" && dayjs().isSame(dayjs(jobPosting.deadline), "day")) ||
        (deadlineChecked === "Under 3 døgn" && dayjs(jobPosting.deadline).isBetween(dayjs().add(3, "day"), dayjs())) ||
        (deadlineChecked === "Innen en uke" && dayjs(jobPosting.deadline).isBetween(dayjs().add(7, "day"), dayjs()))
    )
    .filter(
      /* 
      Filter on published this week. 
      If published checked this week is not checked, return all job postings. 
      Else job posting has to be created this week for it to be shown
      */
      jobPosting =>
        !publishedThisWeekChecked || dayjs(jobPosting.created_at).isBetween(dayjs().subtract(7, "day"), dayjs())
    )
    .filter(
      /* 
      Filter on job posting sector
      */
      jobPosting => !sectorsChecked.length || jobPosting.sectors.some(jobSector => sectorsChecked.includes(jobSector))
    )
    .sort((a, b) => dayjs(a.deadline).valueOf() - dayjs(b.deadline).valueOf());

/*
Return a string that will be displayed with empty icon when there is either 
no jobposting matching the search or no data recived from backend
*/
export const getJobPostingStatusMessage = (jobPostings: Job[]) => {
  if (jobPostings.length) {
    return "Ingen stillingsannonser tilfredstiller ditt søk";
  }
  return "Ingen stillingsannonser er tilgjengelige for øyeblikket";
};
